import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';

import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Router } from '@angular/router';

import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { FooterActionComponent } from '../../../../shared/components/footer-action/footer-action.component';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { AlertCardComponent } from '../../../../shared/components/alert-card/alert-card.component';
import { EnvioDocumentosServicoConfigService } from '../../../../shared/services/apis/servico/envio-documentos-servico-config.service';
import { EnvioDocumentosServicoConfigRequest } from '../../../../shared/services/requests/servico/envio-documentos-servico-config.request';
import { EnvioDocumentosServicoConfigResponse } from '../../../../shared/services/responses/servico/envio-documentos-servico-config.response';

interface VariavelMensagem {
  codigo: string;
  descricao: string;
}

@Component({
  selector: 'app-envio-documentos-servico',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSlideToggleModule,
    HeaderComponent,
    FooterActionComponent,
    LoadingBoxComponent,
    AlertCardComponent,
  ],
  templateUrl: './envio-documentos-servico.component.html',
  styleUrl: './envio-documentos-servico.component.css',
})
export class EnvioDocumentosServicoComponent implements OnInit {
  public isLoading = false;
  public isSaving = false;
  public configCarregada = false;
  public idEnvioDocumentosServicoConfig = 0;
  public tamanhoMaximoAssunto = 150;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Servicos;
  public moduloEnum = ModuloEnum.Configuracoes;

  @ViewChild('nfseAssuntoInput') nfseAssuntoInput!: ElementRef<HTMLInputElement>;
  @ViewChild('nfseMensagemInput') nfseMensagemInput!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('ordemServicoAssuntoInput') ordemServicoAssuntoInput!: ElementRef<HTMLInputElement>;
  @ViewChild('ordemServicoMensagemInput') ordemServicoMensagemInput!: ElementRef<HTMLTextAreaElement>;

  public showVariaveisNfse = false;
  public showVariaveisOrdemServico = false;
  public isNfseAssuntoFocado = false;
  public isOrdemServicoAssuntoFocado = false;

  public form = new FormGroup({
    nfseAssunto: new FormControl('', [Validators.maxLength(this.tamanhoMaximoAssunto)]),
    nfseMensagem: new FormControl(''),
    ordemServicoAssunto: new FormControl('', [Validators.maxLength(this.tamanhoMaximoAssunto)]),
    ordemServicoMensagem: new FormControl(''),
    isEnvioWhatsapp: new FormControl(false),
  });

  public variaveis: VariavelMensagem[] = [
    { codigo: '[NOME_CLIENTE]', descricao: 'Nome/razão social do cliente' },
    { codigo: '[NUMERO_DOCUMENTO]', descricao: 'Número da OS ou da NFSe' },
    { codigo: '[VALOR]', descricao: 'Valor total (R$ 0.000,00)' },
    { codigo: '[DATA_VENCIMENTO]', descricao: 'Vencimento da primeira parcela em aberto' },
    { codigo: '[LINK]', descricao: 'Link público do documento' },
    { codigo: '[NOME_EMPRESA]', descricao: 'Nome fantasia da empresa' },
  ];

  constructor(
    private envioDocumentosServicoConfigService: EnvioDocumentosServicoConfigService,
    private alertService: AlertService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.resetScrollToTop();
    this.carregarConfiguracoes();
  }

  private carregarConfiguracoes(): void {
    this.isLoading = true;
    this.configCarregada = false;

    this.envioDocumentosServicoConfigService.get().subscribe({
      next: (response: EnvioDocumentosServicoConfigResponse) => {
        this.isLoading = false;
        if (response.isError) {
          this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
          this.resetScrollToTop();
          return;
        }

        const config = response.envioDocumentosServicoConfig;
        this.idEnvioDocumentosServicoConfig = config.idEnvioDocumentosServicoConfig;
        this.form.patchValue(config);
        this.configCarregada = true;

        this.resetScrollToTop();
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show(
          'Atenção',
          'Não foi possível concluir a operação no momento. Por favor, aguarde alguns instantes e tente novamente.',
          AlertType.warning
        );
        this.resetScrollToTop();
      },
    });
  }

  private resetScrollToTop(): void {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }

  public salvar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSaving = true;
    const request = this.montarRequest();
    const naoReconhecidas = this.buscarVariaveisNaoReconhecidas(request);

    this.envioDocumentosServicoConfigService.update(this.idEnvioDocumentosServicoConfig, request).subscribe({
      next: (response: EnvioDocumentosServicoConfigResponse) => {
        this.isSaving = false;
        if (response.isError) {
          this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
          return;
        }

        if (naoReconhecidas.length > 0) {
          this.alertService.show(
            'Configuração salva',
            `Variável não reconhecida: ${naoReconhecidas.join(', ')}`,
            AlertType.warning
          );
        } else {
          this.alertService.show('Sucesso', 'Configuração salva com sucesso!', AlertType.success);
        }
        this.voltar();
      },
      error: () => {
        this.isSaving = false;
        this.alertService.show(
          'Atenção',
          'Não foi possível concluir a operação no momento. Por favor, aguarde alguns instantes e tente novamente.',
          AlertType.warning
        );
      },
    });
  }

  private montarRequest(): EnvioDocumentosServicoConfigRequest {
    const formValues = this.form.getRawValue();
    return {
      nfseAssunto: formValues.nfseAssunto ?? '',
      nfseMensagem: formValues.nfseMensagem ?? '',
      ordemServicoAssunto: formValues.ordemServicoAssunto ?? '',
      ordemServicoMensagem: formValues.ordemServicoMensagem ?? '',
      isEnvioWhatsapp: formValues.isEnvioWhatsapp ?? false,
    };
  }

  private buscarVariaveisNaoReconhecidas(request: EnvioDocumentosServicoConfigRequest): string[] {
    const codigosValidos = this.variaveis.map(v => v.codigo);
    const textos = [
      request.nfseAssunto,
      request.nfseMensagem,
      request.ordemServicoAssunto,
      request.ordemServicoMensagem,
    ];
    const codigosUsados = new Set(textos.flatMap(texto => texto.match(/\[[A-Z0-9_]+\]/g) ?? []));
    return [...codigosUsados].filter(codigo => !codigosValidos.includes(codigo));
  }

  public toggleVariaveisNfse(): void {
    this.showVariaveisNfse = !this.showVariaveisNfse;
  }

  public toggleVariaveisOrdemServico(): void {
    this.showVariaveisOrdemServico = !this.showVariaveisOrdemServico;
  }

  public inserirVariavelNfse(codigo: string): void {
    if (this.isNfseAssuntoFocado) {
      this.inserirVariavelNoCampo(
        this.nfseAssuntoInput.nativeElement,
        this.form.controls.nfseAssunto,
        codigo
      );
    } else {
      this.inserirVariavelNoCampo(
        this.nfseMensagemInput.nativeElement,
        this.form.controls.nfseMensagem,
        codigo
      );
    }
  }

  public inserirVariavelOrdemServico(codigo: string): void {
    if (this.isOrdemServicoAssuntoFocado) {
      this.inserirVariavelNoCampo(
        this.ordemServicoAssuntoInput.nativeElement,
        this.form.controls.ordemServicoAssunto,
        codigo
      );
    } else {
      this.inserirVariavelNoCampo(
        this.ordemServicoMensagemInput.nativeElement,
        this.form.controls.ordemServicoMensagem,
        codigo
      );
    }
  }

  private inserirVariavelNoCampo(
    campo: HTMLInputElement | HTMLTextAreaElement,
    control: FormControl<string | null>,
    codigo: string
  ): void {
    const valor = control.value ?? '';
    const inicio = campo.selectionStart ?? valor.length;
    const fim = campo.selectionEnd ?? inicio;
    const posicao = inicio + codigo.length;

    control.setValue(valor.slice(0, inicio) + codigo + valor.slice(fim));
    control.markAsDirty();

    setTimeout(() => {
      campo.focus();
      campo.setSelectionRange(posicao, posicao);
    });
  }

  public voltar(): void {
    this.router.navigate(['/configuracoes/servicos']);
  }
}
